/**
 * TwoFactorForm Component
 *
 * Two-factor authentication form for email-based 2FA.
 * Features:
 * - 6-digit code input with auto-focus
 * - Visual countdown timer for code validity
 * - Error display for invalid code
 * - Account lock message after max consecutive failures
 * - Resend code functionality
 *
 * @validates Requirements 14.4, 14.5
 */

import {
  useState,
  useCallback,
  useEffect,
  useRef,
  type ChangeEvent,
  type KeyboardEvent,
  type ClipboardEvent,
  type FormEvent,
} from 'react';
import { useTranslation } from 'react-i18next';

// ============================================================================
// Types
// ============================================================================

export interface TwoFactorFormProps {
  /** User ID for 2FA verification */
  userId: string;
  /** Session ID for 2FA verification */
  sessionId: string;
  /** Callback when code is submitted */
  onSubmit: (userId: string, sessionId: string, code: string) => Promise<TwoFactorResult>;
  /** Callback to resend code */
  onResend?: (userId: string, sessionId: string) => Promise<{ success: boolean; error?: string }>;
  /** Callback when verification succeeds */
  onSuccess?: () => void;
  /** Callback when account is locked */
  onAccountLocked?: () => void;
  /** Callback to go back to login form */
  onBack?: () => void;
  /** Code validity duration in seconds (default: 300 = 5 minutes) */
  codeValiditySeconds?: number;
  /** Maximum consecutive failures before lock (default: 3) */
  maxAttempts?: number;
}

export interface TwoFactorResult {
  success: boolean;
  error?: string;
  attemptsRemaining?: number;
  isLocked?: boolean;
}

// ============================================================================
// Constants
// ============================================================================

const CODE_LENGTH = 6;
const DEFAULT_VALIDITY_SECONDS = 300; // 5 minutes
const DEFAULT_MAX_ATTEMPTS = 3;
const RESEND_COOLDOWN_SECONDS = 60;

// ============================================================================
// Component
// ============================================================================

export function TwoFactorForm({
  userId,
  sessionId,
  onSubmit,
  onResend,
  onSuccess,
  onAccountLocked,
  onBack,
  codeValiditySeconds = DEFAULT_VALIDITY_SECONDS,
  maxAttempts = DEFAULT_MAX_ATTEMPTS,
}: TwoFactorFormProps) {
  const { t } = useTranslation();
  
  // State for code digits (array of 6 single characters)
  const [digits, setDigits] = useState<string[]>(Array(CODE_LENGTH).fill(''));
  const [isLoading, setIsLoading] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [attemptsRemaining, setAttemptsRemaining] = useState(maxAttempts);
  const [isLocked, setIsLocked] = useState(false);
  const [timeRemaining, setTimeRemaining] = useState(codeValiditySeconds);
  const [resendCooldown, setResendCooldown] = useState(0);

  // Refs for input fields
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  // Auto-focus first input on mount
  useEffect(() => {
    inputRefs.current[0]?.focus();
  }, []);

  // Countdown timer effect
  useEffect(() => {
    if (isLocked) return;

    const timer = setInterval(() => {
      setTimeRemaining((prev) => {
        if (prev <= 1) {
          return 0; // Code expired
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [isLocked]);

  // Resend cooldown timer
  useEffect(() => {
    if (resendCooldown <= 0) return;

    const timer = setInterval(() => {
      setResendCooldown((prev) => Math.max(0, prev - 1));
    }, 1000);

    return () => clearInterval(timer);
  }, [resendCooldown]);

  // Handle digit input change
  const handleDigitChange = useCallback(
    (index: number, e: ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value;

      // iOS autofill can insert the full code at once - detect this
      if (value.length > 1) {
        // Extract only digits
        const digits = value.replace(/\D/g, '');
        if (digits.length > 0) {
          const newDigits = Array(CODE_LENGTH).fill('');
          for (let i = 0; i < Math.min(digits.length, CODE_LENGTH); i++) {
            newDigits[i] = digits[i];
          }
          setDigits(newDigits);
          setError(null);
          // Focus the last filled input or the next empty one
          const lastFilledIndex = Math.min(digits.length - 1, CODE_LENGTH - 1);
          inputRefs.current[lastFilledIndex]?.focus();
          return;
        }
      }

      // Only accept single digit
      if (value && !/^\d$/.test(value)) return;

      const newDigits = [...digits];
      newDigits[index] = value;
      setDigits(newDigits);
      setError(null);

      // Auto-focus next input if digit entered
      if (value && index < CODE_LENGTH - 1) {
        inputRefs.current[index + 1]?.focus();
      }
    },
    [digits]
  );

  // Handle keyboard navigation
  const handleKeyDown = useCallback(
    (index: number, e: KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Backspace') {
        if (!digits[index] && index > 0) {
          // Move to previous input on backspace if current is empty
          inputRefs.current[index - 1]?.focus();
          const newDigits = [...digits];
          newDigits[index - 1] = '';
          setDigits(newDigits);
        }
      } else if (e.key === 'ArrowLeft' && index > 0) {
        inputRefs.current[index - 1]?.focus();
      } else if (e.key === 'ArrowRight' && index < CODE_LENGTH - 1) {
        inputRefs.current[index + 1]?.focus();
      }
    },
    [digits]
  );

  // Handle paste event for full code
  const handlePaste = useCallback((e: ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    const pastedText = e.clipboardData.getData('text').replace(/\D/g, '');

    if (pastedText.length > 0) {
      const newDigits = Array(CODE_LENGTH).fill('');
      for (let i = 0; i < Math.min(pastedText.length, CODE_LENGTH); i++) {
        newDigits[i] = pastedText[i];
      }
      setDigits(newDigits);
      setError(null);

      // Focus the last filled input or the next empty one
      const lastFilledIndex = Math.min(pastedText.length - 1, CODE_LENGTH - 1);
      inputRefs.current[lastFilledIndex]?.focus();
    }
  }, []);

  // Handle resend code
  const handleResend = useCallback(async () => {
    if (!onResend || resendCooldown > 0 || isResending) return;

    setIsResending(true);
    setError(null);

    try {
      const result = await onResend(userId, sessionId);
      if (result.success) {
        setTimeRemaining(codeValiditySeconds);
        setResendCooldown(RESEND_COOLDOWN_SECONDS);
        setDigits(Array(CODE_LENGTH).fill(''));
        inputRefs.current[0]?.focus();
      } else {
        setError(result.error || t('auth.twoFactor.resendError'));
      }
    } catch {
      setError(t('auth.twoFactor.resendError'));
    } finally {
      setIsResending(false);
    }
  }, [onResend, userId, sessionId, resendCooldown, isResending, codeValiditySeconds, t]);

  // Form submission handler
  const handleSubmit = useCallback(
    async (e: FormEvent) => {
      e.preventDefault();

      if (isLocked) return;

      const code = digits.join('');
      if (code.length !== CODE_LENGTH) {
        setError(t('auth.twoFactor.enterAllDigits'));
        return;
      }

      setIsLoading(true);
      setError(null);

      try {
        const result = await onSubmit(userId, sessionId, code);

        if (result.success) {
          onSuccess?.();
        } else {
          // Update attempts remaining
          const remaining = result.attemptsRemaining ?? attemptsRemaining - 1;
          setAttemptsRemaining(remaining);

          if (result.isLocked || remaining <= 0) {
            setIsLocked(true);
            setError(t('auth.twoFactor.accountLocked'));
            onAccountLocked?.();
          } else {
            setError(result.error || t('auth.twoFactor.invalidCode'));
            // Clear code for retry
            setDigits(Array(CODE_LENGTH).fill(''));
            // Focus first input
            inputRefs.current[0]?.focus();
          }
        }
      } catch {
        setError(t('auth.twoFactor.connectionError'));
      } finally {
        setIsLoading(false);
      }
    },
    [
      digits,
      userId,
      sessionId,
      onSubmit,
      onSuccess,
      onAccountLocked,
      isLocked,
      attemptsRemaining,
      t,
    ]
  );

  // Check if code is complete
  const isCodeComplete = digits.every((d) => d !== '');

  // Format time remaining
  const formatTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Timer progress percentage
  const timerProgress = (timeRemaining / codeValiditySeconds) * 100;

  // Timer color based on remaining time
  const getTimerColor = (): string => {
    if (timeRemaining <= 30) return 'text-red-500';
    if (timeRemaining <= 60) return 'text-orange-500';
    return 'text-violet-600';
  };

  if (isLocked) {
    return (
      <div className="text-center">
        <div className="w-16 h-16 mx-auto mb-4 bg-red-100 rounded-full flex items-center justify-center">
          <LockIcon className="w-8 h-8 text-red-500" />
        </div>
        <h2 className="text-xl font-semibold text-slate-900 mb-2">
          {t('auth.twoFactor.lockedTitle')}
        </h2>
        <p className="text-slate-600 mb-4">
          {t('auth.twoFactor.lockedMessage', { attempts: maxAttempts })}
        </p>
        <p className="text-sm text-slate-500 mb-6">
          {t('auth.twoFactor.lockedHelp')}
        </p>
        {onBack && (
          <button
            type="button"
            onClick={onBack}
            className="text-violet-600 hover:text-violet-700 font-medium"
          >
            {t('auth.twoFactor.backToLogin')}
          </button>
        )}
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="text-center mb-6">
        <div className="w-16 h-16 mx-auto mb-4 bg-violet-100 rounded-full flex items-center justify-center">
          <ShieldIcon className="w-8 h-8 text-violet-600" />
        </div>
        <h2 className="text-xl font-semibold text-slate-900 mb-2">
          {t('auth.twoFactor.title')}
        </h2>
        <p className="text-slate-600 text-sm">
          {t('auth.twoFactor.emailSent')}
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Code Input Fields */}
        <div
          className="flex justify-center gap-2 md:gap-3"
          role="group"
          aria-label={t('auth.twoFactor.codeLabel')}
        >
          {digits.map((digit, index) => (
            <input
              key={index}
              ref={(el) => {
                inputRefs.current[index] = el;
              }}
              type="text"
              inputMode="numeric"
              maxLength={index === 0 ? 6 : 1}
              autoComplete={index === 0 ? 'one-time-code' : 'off'}
              value={digit}
              onChange={(e) => handleDigitChange(index, e)}
              onKeyDown={(e) => handleKeyDown(index, e)}
              onPaste={handlePaste}
              disabled={isLoading || timeRemaining === 0}
              aria-label={`${t('auth.twoFactor.digit')} ${index + 1}`}
              className={`
                w-12 h-14 md:w-14 md:h-16
                text-center text-2xl font-mono font-bold
                border-2 rounded-lg
                transition-all duration-150
                focus:outline-none focus:ring-2 focus:ring-offset-2
                ${
                  digit
                    ? 'border-violet-400 bg-violet-50 focus:border-violet-500 focus:ring-violet-500'
                    : 'border-slate-300 bg-white focus:border-violet-500 focus:ring-violet-500'
                }
                ${isLoading || timeRemaining === 0 ? 'opacity-50 cursor-not-allowed' : ''}
                ${error ? 'border-red-400 bg-red-50' : ''}
              `}
            />
          ))}
        </div>

        {/* Timer Section */}
        <div className="flex flex-col items-center gap-2">
          <div className="flex items-center gap-2">
            <ClockIcon className={`w-4 h-4 ${getTimerColor()}`} />
            <span className={`text-sm font-medium ${getTimerColor()}`}>
              {timeRemaining > 0 
                ? t('auth.twoFactor.codeValidFor', { time: formatTime(timeRemaining) })
                : t('auth.twoFactor.codeExpired')
              }
            </span>
          </div>
          {/* Timer Progress Bar */}
          <div className="w-full h-1.5 bg-slate-200 rounded-full overflow-hidden">
            <div
              className={`h-full rounded-full transition-all duration-1000 ease-linear ${
                timeRemaining <= 30
                  ? 'bg-red-500'
                  : timeRemaining <= 60
                  ? 'bg-orange-500'
                  : 'bg-violet-500'
              }`}
              style={{ width: `${timerProgress}%` }}
              role="progressbar"
              aria-valuenow={timeRemaining}
              aria-valuemin={0}
              aria-valuemax={codeValiditySeconds}
              aria-label={t('auth.twoFactor.timeRemaining')}
            />
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div
            role="alert"
            className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm"
          >
            <ErrorIcon className="w-5 h-5 flex-shrink-0" />
            <span>{error}</span>
          </div>
        )}

        {/* Attempts Remaining */}
        {attemptsRemaining < maxAttempts && (
          <p className="text-center text-sm text-orange-600">
            {t('auth.twoFactor.attemptsRemaining', { count: attemptsRemaining })}
          </p>
        )}

        {/* Submit Button */}
        <button
          type="submit"
          disabled={!isCodeComplete || isLoading || timeRemaining === 0}
          className={`
            w-full
            flex items-center justify-center gap-2
            px-6 py-3 rounded-lg
            font-medium text-white
            transition-all duration-150
            focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-violet-500
            ${
              !isCodeComplete || isLoading || timeRemaining === 0
                ? 'bg-slate-300 cursor-not-allowed'
                : 'bg-violet-600 hover:bg-violet-700'
            }
          `}
        >
          {isLoading ? (
            <>
              <LoadingSpinner className="w-5 h-5" />
              <span>{t('auth.twoFactor.verifying')}</span>
            </>
          ) : (
            <>
              <CheckIcon className="w-5 h-5" />
              <span>{t('auth.twoFactor.verify')}</span>
            </>
          )}
        </button>
      </form>

      {/* Resend Code */}
      {onResend && (
        <div className="mt-6 text-center">
          <p className="text-sm text-slate-500 mb-2">
            {t('auth.twoFactor.noCode')}
          </p>
          <button
            type="button"
            onClick={handleResend}
            disabled={resendCooldown > 0 || isResending}
            className={`
              text-sm font-medium
              focus:outline-none focus:underline
              ${
                resendCooldown > 0 || isResending
                  ? 'text-slate-400 cursor-not-allowed'
                  : 'text-violet-600 hover:text-violet-700'
              }
            `}
          >
            {isResending
              ? t('auth.twoFactor.resending')
              : resendCooldown > 0
              ? t('auth.twoFactor.resendIn', { seconds: resendCooldown })
              : t('auth.twoFactor.resend')
            }
          </button>
        </div>
      )}

      {/* Back to Login */}
      {onBack && (
        <div className="mt-4 text-center">
          <button
            type="button"
            onClick={onBack}
            className="text-sm text-slate-500 hover:text-slate-700"
          >
            {t('auth.twoFactor.backToLogin')}
          </button>
        </div>
      )}
    </div>
  );
}

// ============================================================================
// Icon Components
// ============================================================================

function ShieldIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={2}
      aria-hidden="true"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M9 12.75L11.25 15 15 9.75m-3-7.036A11.959 11.959 0 013.598 6 11.99 11.99 0 003 9.749c0 5.592 3.824 10.29 9 11.623 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.571-.598-3.751h-.152c-3.196 0-6.1-1.248-8.25-3.285z"
      />
    </svg>
  );
}

function LockIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={2}
      aria-hidden="true"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M16.5 10.5V6.75a4.5 4.5 0 10-9 0v3.75m-.75 11.25h10.5a2.25 2.25 0 002.25-2.25v-6.75a2.25 2.25 0 00-2.25-2.25H6.75a2.25 2.25 0 00-2.25 2.25v6.75a2.25 2.25 0 002.25 2.25z"
      />
    </svg>
  );
}

function ClockIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={2}
      aria-hidden="true"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M12 6v6h4.5m4.5 0a9 9 0 11-18 0 9 9 0 0118 0z"
      />
    </svg>
  );
}

function ErrorIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={2}
      aria-hidden="true"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z"
      />
    </svg>
  );
}

function CheckIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={2}
      aria-hidden="true"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M4.5 12.75l6 6 9-13.5"
      />
    </svg>
  );
}

function LoadingSpinner({ className }: { className?: string }) {
  return (
    <svg
      className={`${className} animate-spin`}
      fill="none"
      viewBox="0 0 24 24"
      aria-hidden="true"
    >
      <circle
        className="opacity-25"
        cx="12"
        cy="12"
        r="10"
        stroke="currentColor"
        strokeWidth="4"
      />
      <path
        className="opacity-75"
        fill="currentColor"
        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
      />
    </svg>
  );
}

export default TwoFactorForm;
